import React, { useState, useMemo } from 'react';
import { View, StyleSheet, Text, ScrollView } from 'react-native';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLegend, VictoryStack, VictoryVoronoiContainer } from 'victory-native';
import { FencelineChartConfig as C } from './fencelineChartStyles';

interface StackedBarChartProps {
  label: string[];
  data: any;
  ChartTitle: string;
  subTitle?: string;
  legend?: any;
  threshold?: number;
}

function StackedBarChartComp(props: StackedBarChartProps) {
  const [containerWidth, setContainerWidth] = useState(400);
  const n = props.data.labels.length;
  const chartWidth = n <= 30 ? containerWidth : Math.max(containerWidth, n * C.scrollPerLabel);
  const barRatio = n > 30 ? C.barRatio.dense : C.barRatio.normal;

  const transformedDatasets = useMemo(() => {
    return props.data.datasets.map((dataset: any) => ({
      data: dataset.data.map((value: any) => ({
        x: value.x,
        y: parseFloat(value.y.toFixed(2)),
      })),
      color: dataset.color,
    }));
  }, [props.data]);

  return (
    <View
      style={styles.container}
      onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}
    >
      {props.data.datasets.length === 0 ? (
        <Text style={styles.noDataText}>{`No data available for ${props.ChartTitle}`}</Text>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ width: '100%' }}>
        <VictoryChart
          width={chartWidth}
          height={C.height}
          domainPadding={
            n > 10 ? { x: 10 }
              : n > 4 ? { x: 50 }
              : { x: 70 }
          }
          padding={C.padding.multi}
          containerComponent={
            <VictoryVoronoiContainer labels={({ datum }) => `${datum.x}: ${datum.y.toFixed(2)}`} />
          }
        >
          <VictoryLegend
            x={10} y={20}
            orientation="horizontal"
            gutter={20}
            style={{ labels: { fontSize: C.legendFontSize } }}
            data={props.data.datasets.map((dataset: any) => ({
              name: dataset.name,
              symbol: { fill: dataset.color ? dataset.color(1) : '#000' },
            }))}
          />
          <VictoryAxis
            tickFormat={t => (t.length > 10 ? `${t.slice(0, 8)}...` : t)}
            style={{ tickLabels: { fontSize: C.tickFontSize, angle: n > 5 ? 60 : 45, textAnchor: 'start' } }}
          />
          <VictoryAxis dependentAxis tickFormat={t => `${t.toFixed(2)}`} style={{ tickLabels: { fontSize: C.tickFontSize } }} />
          <VictoryStack colorScale={props.data.datasets.map((d: any) => d.color ? d.color(1) : '#000')}>
            {transformedDatasets.map((dataset: any, index: any) => (
              <VictoryBar
                key={index}
                data={dataset.data}
                barRatio={barRatio}
                animate={{ duration: 800, onLoad: { duration: 500 } }}
              />
            ))}
          </VictoryStack>
        </VictoryChart>
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: '100%', alignItems: 'center' },
  noDataText: { fontSize: 14, textAlign: 'center', color: '#999', padding: 20 },
});

export default StackedBarChartComp;
